<script setup lang="ts">
import { getRegulatorList, type RegulatorListAreaItem } from '~/api/regulator-list'

definePageMeta({
  name: 'regulators',
})

type RegulatorTab = {
  key: string
  label: string
  areaCodeType: string
  areaCode: string
}

const { localePath, t } = useAppI18n()

const REGULATOR_TABS: RegulatorTab[] = [{ key: 'all:', label: t('83794'), areaCodeType: 'all', areaCode: '' }]

const breadcrumbItems = [
  {
    label: t('82520'),
    route: '/',
  },
  {
    label: t('84679'),
  },
]

const activeTabKey = ref(REGULATOR_TABS[0]?.key ?? 'all:')

/** 将 BFF 地区项转成页面 tab，key 同时包含类型和值以避免空 code 冲突。 */
function normalizeAreaTab(item: RegulatorListAreaItem): RegulatorTab {
  return {
    key: `${item.areaCodeType}:${item.areaCode}`,
    label: item.areaName,
    areaCodeType: item.areaCodeType,
    areaCode: item.areaCode,
  }
}

/** 根据当前选中的地区 tab 拼接接口筛选参数。 */
function buildRegulatorListParams(tabKey: string) {
  const [areaCodeType = 'all', areaCode = ''] = tabKey.split(':')
  if (areaCodeType === 'all') {
    return {}
  }

  return {
    areaCodeType,
    ...(areaCode ? { areaCode } : {}),
  }
}

const {
  data: regulatorListData,
  error: regulatorListError,
  pending: regulatorListPending,
  refresh: refreshRegulatorList,
} = await useAsyncData('regulator-list', () => getRegulatorList(buildRegulatorListParams(activeTabKey.value)), {
  default: () => ({
    items: [],
    areaItems: [],
    regulatorsDescription: '',
  }),
})

if (regulatorListError.value) {
  throw regulatorListError.value
}

const regulatorTabs = computed(() => {
  const areaItems = regulatorListData.value?.areaItems || []
  if (!areaItems.length) {
    return REGULATOR_TABS
  }

  return areaItems.map((item) => normalizeAreaTab(item))
})

const regulatorItems = computed(() => regulatorListData.value?.items || [])

/** 切换监管地区分类并重新拉取对应列表。 */
const handleTabClick = async (tab: RegulatorTab) => {
  activeTabKey.value = tab.key
  await refreshRegulatorList()
  if (regulatorListError.value) {
    throw regulatorListError.value
  }
}

/** 使用列表接口返回的 urlSlug 生成监管机构详情路由。 */
const getRegulatorDetailPath = (urlSlug: string) => localePath(`/regulators/${urlSlug}`)

useSeoMeta({
  title: t('84877'),
  description: t('84876'),
})
</script>

<template>
  <main class="min-w-[1200px] bg-[#ffffff] text-[#292a2e]">
    <section class="bg-[linear-gradient(180deg,#EAF3FF_39.42%,#fff_100%)] pt-[70px]">
      <div class="mx-auto w-[1200px]">
        <CommonBreadcrumb :items="breadcrumbItems" class="!py-[24px]" />

        <div class="flex min-h-[228px] items-center justify-between pb-[36px]">
          <div class="w-[760px] shrink-0 pt-[12px]">
            <h1 class="text-[42px] leading-[58px] font-[600] tracking-[0] text-[#292a2e]">{{ t('84679') }}</h1>
            <p class="mt-[12px] text-[16px] leading-[26px] font-[400] text-[#7d818a]">
              {{ regulatorListData?.regulatorsDescription || t('84680') }}
            </p>
          </div>

          <div class="mr-[2px] h-[146px] w-[265px] shrink-0 rtl:mr-0 rtl:ml-[2px]">
            <CommonImage src="https://resources1.interface003.com/web30/2026/07/24/aca9bc5a-71ce-4886-b9d7-3d5ee1bc2c6c.png" :alt="t('84679')" class="size-full" object-fit="contain" loading="eager" />
          </div>
        </div>
      </div>
    </section>

    <section class="mx-auto w-[1200px] pb-[80px]">
      <nav
        :aria-label="t('82502')"
        class="flex h-[60px] items-end overflow-x-auto border-b border-[#e8ebf0] bg-[#ffffff]"
      >
        <button
          v-for="tab in regulatorTabs"
          :key="tab.key"
          type="button"
          class="relative mr-[28px] h-[60px] min-w-[34px] shrink-0 cursor-pointer text-[20px] leading-[28px] font-[400] whitespace-nowrap text-[#8c8f97] transition-colors hover:text-[#292a2e] rtl:mr-0 rtl:ml-[24px]"
          :class="{
            'font-[600] text-[#292a2e]': activeTabKey === tab.key,
          }"
          @click="handleTabClick(tab)"
        >
          <span class="inline-flex h-full items-center">{{ tab.label }}</span>
          <span
            v-if="activeTabKey === tab.key"
            class="absolute bottom-0 left-1/2 h-[4px] w-[34px] -translate-x-1/2 rounded-[999px] bg-[#292A2E] rtl:right-1/2 rtl:left-auto rtl:translate-x-1/2"
          ></span>
        </button>
      </nav>

      <div class="flex flex-col">
        <NuxtLink
          v-for="item in regulatorItems"
          :key="item.regulatorId || item.regulatorCode"
          :to="getRegulatorDetailPath(item.urlSlug)"
          target="_blank"
          class="flex h-[152px] cursor-pointer items-center gap-[40px] border-b border-[rgba(5,21,36,0.06)] bg-[#ffffff] py-[32px] transition-colors hover:bg-[#f8f8f8]"
        >
          <div class="flex min-w-0 flex-1 items-center gap-[24px]">
            <div
              class="flex h-[76px] w-[134px] shrink-0 items-center justify-center overflow-hidden rounded-[4px] border border-[rgba(5,21,36,0.06)] bg-[#ffffff]"
            >
              <CommonImage :src="item.logo" :alt="item.regulatorShowName" class="size-full" object-fit="contain" />
            </div>

            <div class="min-w-0 flex-1">
              <h2 class="truncate text-[20px] leading-[28px] font-[600] text-[#292a2e]">
                {{ item.regulatorShowName || '--' }}
              </h2>
              <div class="mt-[8px] flex items-center gap-[12px] text-[14px] leading-[22px] font-[400] text-[#505258]">
                <div class="flex items-center gap-[4px]">
                  <CommonImage
                    :src="item.countryFlag"
                    :alt="item.countryName"
                    class="!h-[14px] !w-[22px] !rounded-[2px]"
                    object-fit="cover"
                  />
                  <span>{{ item.countryName || '--' }}</span>
                </div>
                <span class="h-[16px] w-[1px] bg-[rgba(5,21,36,0.12)]"></span>
                <span>{{ item.foundationDate || '--' }}</span>
                <span class="h-[16px] w-[1px] bg-[rgba(5,21,36,0.12)]"></span>
                <span>{{ item.isOffshore || '--' }}</span>
              </div>
              <p class="mt-[8px] line-clamp-1 text-[14px] leading-[22px] font-[400] text-[#7d818a]">
                {{ item.regulatorDescription || '--' }}
              </p>
            </div>
          </div>

          <span
            class="flex h-[32px] w-[86px] shrink-0 items-center justify-center rounded-[1000px] bg-[#112D73] px-[4px] py-[8px] text-[12px] leading-[18px] font-[500] text-[#ffffff]"
          >
            {{ t('84342') }}
          </span>
        </NuxtLink>

        <div
          v-if="!regulatorListPending && !regulatorItems.length"
          class="flex h-[180px] items-center justify-center border-b border-[#edf0f4] text-[14px] leading-[22px] text-[#8a9099]"
        >
          {{ t('83714') }}
        </div>
      </div>
    </section>
  </main>
</template>
